/* global __OFISGOREV_VERSION__ */
import {useEffect} from 'react';
import {useLocation} from 'react-router-dom';
import MobileBarcodeScannerBridge from './MobileBarcodeScannerBridge';
import WarehouseStockBehaviorBridge from './WarehouseStockBehaviorBridge';
import WarehouseScanAliasBridge from './WarehouseScanAliasBridge';
import './WarehouseUxBridge.css';

const APP_VERSION=__OFISGOREV_VERSION__;
const themedRoutes=[
 '/depo/mal-kabul',
 '/depo/parti-izlenebilirlik',
 '/depo/standart-kesimler',
 '/depo/uretim-verimliligi',
];
const wideLayoutRoutes=[
 '/depo/mal-kabul',
 '/depo/iadeler',
 '/depo/stok-sayim',
];

const hasText=(node,text)=>String(node?.textContent||'').trim().includes(text);
const isInteractive=node=>!!node?.closest?.('button,a,input,select,textarea,label');

function bindCollapse(container,header,defaultCollapsed=true){
 if(!container||!header||container.dataset.warehouseCollapseBound==='1')return;
 container.dataset.warehouseCollapseBound='1';
 container.dataset.collapsed=defaultCollapsed?'true':'false';
 header.classList.add('warehouse-collapse-header');
 const toggle=event=>{
  if(isInteractive(event.target)&&event.target!==header)return;
  container.dataset.collapsed=container.dataset.collapsed==='true'?'false':'true';
 };
 header.addEventListener('click',toggle);
 container._warehouseCollapseCleanup=()=>header.removeEventListener('click',toggle);
}

function polishReceiving(main){
 const sections=[...main.querySelectorAll('section')];
 const left=sections.find(x=>hasText(x.firstElementChild,'1. Stok Kartı Seç'));
 const right=sections.find(x=>hasText(x.firstElementChild,'2. Mal Kabul Bilgileri'));
 [left,right].forEach((section,index)=>{
  if(!section)return;
  section.classList.add(index===0?'warehouse-tone-blue':'warehouse-tone-green','warehouse-receiving-card');
  const button=section.querySelector(':scope > button');
  if(button&&section.dataset.defaultCollapsedApplied!=='1'){
   section.dataset.defaultCollapsedApplied='1';
   if(section.children.length>1)button.click();
  }
 });
 const recent=sections.find(x=>hasText(x.firstElementChild,'Son Mal Kabuller'));
 if(recent)recent.classList.add('warehouse-tone-purple');
 const fifo=sections.find(x=>hasText(x.firstElementChild,'Parti / FIFO Katmanları'));
 if(fifo)fifo.classList.add('warehouse-tone-amber');
}

function polishTraceability(main){
 const aside=main.querySelector('aside');
 if(aside){
  aside.classList.add('warehouse-tone-blue','warehouse-trace-picker');
  const header=aside.firstElementChild;
  bindCollapse(aside,header,true);
 }
 const contentMain=main.querySelector('main');
 if(!contentMain)return;
 const blocks=[...contentMain.querySelectorAll(':scope > section, :scope > div.border.rounded-2xl')];
 const tones=['warehouse-tone-green','warehouse-tone-blue','warehouse-tone-amber','warehouse-tone-purple'];
 blocks.forEach((block,index)=>block.classList.add(tones[index%tones.length]));
}

function polishStandardCuts(main){
 const form=[...main.querySelectorAll('div.border.rounded-2xl.bg-card.p-5')].find(x=>!x.parentElement?.classList.contains('lg:grid-cols-2'));
 if(form)form.classList.add('warehouse-tone-blue');
 const cards=[...main.querySelectorAll('div.border.rounded-2xl.bg-card.p-5')].filter(x=>x.parentElement?.classList.contains('lg:grid-cols-2'));
 const tones=['warehouse-tone-blue','warehouse-tone-green','warehouse-tone-amber','warehouse-tone-purple'];
 cards.forEach((card,index)=>{
  card.classList.add('warehouse-standard-cut-card',tones[index%tones.length]);
  bindCollapse(card,card.firstElementChild,true);
 });
}

function polishEfficiency(main){
 const root=[...main.querySelectorAll('div')].find(x=>x.firstElementChild&&hasText(x.firstElementChild,'Filtreler')&&x.classList.contains('rounded-2xl')&&x.classList.contains('border'));
 if(root){
  root.classList.add('warehouse-tone-blue','warehouse-efficiency-filter');
  bindCollapse(root,root.firstElementChild,true);
 }
 const stats=[...main.querySelectorAll('div.rounded-2xl.border.bg-card.p-5.shadow-sm')];
 const tones=['warehouse-tone-blue','warehouse-tone-green','warehouse-tone-amber','warehouse-tone-purple','warehouse-tone-rose','warehouse-tone-sky'];
 stats.forEach((card,index)=>card.classList.add(tones[index%tones.length]));
 const tables=[...main.querySelectorAll('div.rounded-2xl.border.bg-card.overflow-hidden')];
 tables.forEach((card,index)=>{
  card.classList.add('warehouse-efficiency-table',tones[(index+1)%tones.length]);
  bindCollapse(card,card.firstElementChild,true);
 });
}

export default function WarehouseUxBridge(){
 const location=useLocation();
 useEffect(()=>{
  const wide=wideLayoutRoutes.some(route=>location.pathname.startsWith(route));
  document.body.classList.toggle('warehouse-wide-layout',wide);
  return()=>document.body.classList.remove('warehouse-wide-layout');
 },[location.pathname]);

 useEffect(()=>{
  const themed=themedRoutes.some(route=>location.pathname.startsWith(route));
  document.body.classList.toggle('warehouse-soft-theme',themed);
  if(!themed)return()=>document.body.classList.remove('warehouse-soft-theme');

  const main=document.querySelector('main');
  if(!main)return()=>document.body.classList.remove('warehouse-soft-theme');
  let timer=null;
  const apply=()=>{
   window.clearTimeout(timer);
   timer=window.setTimeout(()=>{
    if(location.pathname.startsWith('/depo/mal-kabul'))polishReceiving(main);
    if(location.pathname.startsWith('/depo/parti-izlenebilirlik'))polishTraceability(main);
    if(location.pathname.startsWith('/depo/standart-kesimler'))polishStandardCuts(main);
    if(location.pathname.startsWith('/depo/uretim-verimliligi'))polishEfficiency(main);
   },30);
  };
  const observer=new MutationObserver(apply);
  observer.observe(main,{childList:true,subtree:true});
  apply();
  return()=>{
   observer.disconnect();
   window.clearTimeout(timer);
   main.querySelectorAll('[data-warehouse-collapse-bound="1"]').forEach(node=>node._warehouseCollapseCleanup?.());
   document.body.classList.remove('warehouse-soft-theme');
  };
 },[location.pathname]);

 return <>
  <MobileBarcodeScannerBridge/>
  <WarehouseStockBehaviorBridge/>
  <WarehouseScanAliasBridge/>
  <div
   aria-label={`OfisGörev ${APP_VERSION}`}
   className="fixed right-2 bottom-2 z-40 pointer-events-none select-none rounded-md border border-slate-200/80 bg-white/80 px-2 py-1 text-[10px] font-semibold tracking-wide text-slate-500 shadow-sm backdrop-blur-sm"
  >{APP_VERSION}</div>
 </>;
}
